import {
  CustomFieldType,
  MetastructureFieldType,
  type CustomFieldDefinition
} from '__types__';
import {
  FALLBACK_LABEL,
  shortlistTypeFieldsOf,
  shownByType
} from 'v5/features/shortlists/display/shortlistDisplayFields.logic';
import type { ResolvedMetastructureField } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';

export type ResourceFieldGroupId = 'profile' | 'contact' | 'social' | 'custom';

export interface ResourceFieldOption {
  key: string;
  label: string;
}

export interface ResourceFieldGroup {
  id: ResourceFieldGroupId;
  /** Names of the resource types its fields belong to; null when every type has them. */
  onlyTypes: string[] | null;
  options: ResourceFieldOption[];
}

/** The profile's own fields a card can show, grouped as the profile reads. */
const FIXED_GROUPS: ReadonlyArray<{
  id: ResourceFieldGroupId;
  keys: ReadonlyArray<string>;
}> = [
  {
    id: 'profile',
    keys: [
      'locations',
      'rates',
      'organisation',
      'represented_by',
      'tags',
      'groups'
    ]
  },
  { id: 'contact', keys: ['websites', 'emails', 'phone_numbers'] },
  {
    id: 'social',
    keys: [
      'social_instagram',
      'social_twitter',
      'social_behance',
      'social_youtube',
      'social_vimeo'
    ]
  }
];

/** Kinds the card has no text for. */
const OFF_CARD_TYPES: ReadonlyArray<string> = [
  CustomFieldType.NETSUITE_MASTER_PROJECT
];

export interface ResourceFieldCatalogInput {
  /** The resource types of the browsing scope, each with its resolved metastructure. */
  types: ReadonlyArray<{
    name: string;
    fields: ReadonlyArray<ResolvedMetastructureField>;
  }>;
}

/**
 * Every field a card can show in this scope: the profile's own fields, then
 * the custom fields some resource type has, one group per set of types that
 * has them. A group whose fields only some types have names those types; a
 * field no type has is left out.
 */
export function resourceFieldCatalog({
  types
}: ResourceFieldCatalogInput): ResourceFieldGroup[] {
  const typeFields = types.map(type => ({
    name: type.name,
    fields: shortlistTypeFieldsOf(type.fields)
  }));
  const typesOf = (key: string): string[] | null => {
    const having = typeFields.filter(type => shownByType(key, type.fields));
    if (having.length === 0) return [];
    return having.length === typeFields.length
      ? null
      : having.map(type => type.name);
  };
  const sameTypes = (a: string[] | null, b: string[] | null) =>
    a === b || (a !== null && b !== null && a.join('\n') === b.join('\n'));

  const fixed = FIXED_GROUPS.map(group => {
    const members = group.keys
      .map(key => ({ key, types: typesOf(key) }))
      .filter(member => member.types === null || member.types.length > 0);
    const [first] = members;
    return {
      id: group.id,
      onlyTypes:
        first && members.every(member => sameTypes(member.types, first.types))
          ? first.types
          : null,
      options: members.map(member => ({
        key: member.key,
        label: FALLBACK_LABEL[member.key] ?? member.key
      }))
    };
  });

  const definitions = new Map<number, CustomFieldDefinition>();
  for (const type of types) {
    for (const field of type.fields) {
      if (
        field.type === MetastructureFieldType.Custom &&
        field.active &&
        !field.customFieldDefinition.archived &&
        !OFF_CARD_TYPES.includes(field.customFieldDefinition.data_type)
      ) {
        definitions.set(
          field.customFieldDefinition.id,
          field.customFieldDefinition
        );
      }
    }
  }
  const custom: ResourceFieldGroup[] = [];
  for (const definition of [...definitions.values()].sort((a, b) =>
    a.name.localeCompare(b.name)
  )) {
    const key = `custom_${definition.id}`;
    const onlyTypes = typesOf(key);
    if (onlyTypes !== null && onlyTypes.length === 0) continue;
    const option = { key, label: definition.name };
    const group = custom.find(candidate =>
      sameTypes(candidate.onlyTypes, onlyTypes)
    );
    if (group) group.options.push(option);
    else custom.push({ id: 'custom', onlyTypes, options: [option] });
  }
  custom.sort(
    (a, b) =>
      Number(a.onlyTypes !== null) - Number(b.onlyTypes !== null) ||
      (a.onlyTypes ?? [])
        .join(', ')
        .localeCompare((b.onlyTypes ?? []).join(', '))
  );

  return [...fixed, ...custom].filter(group => group.options.length > 0);
}

/**
 * The label of a picked key: from the catalog, else from the custom field's
 * definition (a field of a type outside this scope), else the key itself.
 */
export function resourceFieldLabel(
  key: string,
  groups: ReadonlyArray<ResourceFieldGroup>,
  definitions: Readonly<Record<number, CustomFieldDefinition>>
): string {
  for (const group of groups) {
    const option = group.options.find(candidate => candidate.key === key);
    if (option) return option.label;
  }
  if (key.startsWith('custom_')) {
    const definition = definitions[Number(key.slice('custom_'.length))];
    if (definition) return definition.name;
  }
  return FALLBACK_LABEL[key] ?? key;
}
